import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Users, MagnifyingGlass, LockKey, DownloadSimple, Funnel, PencilSimple, Eye, UploadSimple, GraduationCap, Phone, Buildings, Briefcase, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [interestFilter, setInterestFilter] = useState("all");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const i = searchParams.get("interest");
    if (i) setInterestFilter(i);
  }, [searchParams]);
  const [sortBy, setSortBy] = useState("name");
  const [manualDialog, setManualDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [resetDialog, setResetDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "", email: "", role: "",
    phone: "", school_name: "", position: "", interest_type: ""
  });
  const [resetPassword, setResetPassword] = useState("");
  const [manualAmount, setManualAmount] = useState("");
  const [manualEnrollmentId, setManualEnrollmentId] = useState("");
  const [manualNotes, setManualNotes] = useState("");
  const [manualStudentInfo, setManualStudentInfo] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "student", programme_ids: [] });
  const [allRoles, setAllRoles] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [categoryCounts, setCategoryCounts] = useState({ free: 0, programme: 0, pod: 0, all: 0 });
  const [programmeList, setProgrammeList] = useState([]);
  const [selectedProgramme, setSelectedProgramme] = useState(null);
  const [programmeStudents, setProgrammeStudents] = useState([]);
  const [podSubs, setPodSubs] = useState([]);
  const [admissionDialog, setAdmissionDialog] = useState(false);
  const [admissionUploading, setAdmissionUploading] = useState(false);
  // Enrol-existing-user-into-programme controls (inside User Details dialog)
  const [enrollProgrammeId, setEnrollProgrammeId] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [suspendBusy, setSuspendBusy] = useState({}); // { [enrollment_id]: true }
  // Round 44 — search + eligibility filter for programme drill-down
  const [progStudentSearch, setProgStudentSearch] = useState("");
  const [progEligibleOnly, setProgEligibleOnly] = useState(true);

  const fetchUsers = () => {
    api.get("/users").then(r => { setUsers(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => {
    fetchUsers();
    api.get("/roles").then(r => setAllRoles(r.data)).catch(() => {});
    api.get("/users/categories").then(r => setCategoryCounts(r.data)).catch(() => {});
    api.get("/programmes").then(r => setProgrammeList(r.data || [])).catch(() => {});
  }, []);

  // Programme tab → reset drill-down. POD tab → fetch subscribers.
  useEffect(() => {
    if (activeCategory === "programme") {
      setSelectedProgramme(null);
      setProgrammeStudents([]);
    }
    if (activeCategory === "pod") {
      api.get("/pod/subscribers").then(r => setPodSubs(r.data || [])).catch(() => setPodSubs([]));
    }
  }, [activeCategory]);

  const loadProgrammeStudents = async (progId) => {
    setSelectedProgramme(programmeList.find(p => p.id === progId));
    try {
      const params = new URLSearchParams();
      if (progEligibleOnly) params.append("eligible_only", "true");
      if (progStudentSearch) params.append("q", progStudentSearch);
      const r = await api.get(`/programmes/${progId}/students?${params.toString()}`);
      setProgrammeStudents(r.data.students || []);
    } catch (e) {
      toast.error("Failed to load programme students");
    }
  };

  // Round 44 — refresh drill-down when the search or eligibility toggle changes.
  useEffect(() => {
    if (selectedProgramme?.id) loadProgrammeStudents(selectedProgramme.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progStudentSearch, progEligibleOnly]);

  const uploadAdmissionLetter = async (file) => {
    if (!selectedUser || !file) return;
    setAdmissionUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/users/${selectedUser.id}/admission-letter`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Admission letter uploaded");
      setAdmissionDialog(false);
      try {
        const r = await api.get(`/users/${selectedUser.id}/details`);
        setDetailsData(r.data);
      } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
    setAdmissionUploading(false);
  };

  const updateRole = async (userId, role) => {
    try {
      await api.put(`/users/${userId}/role`, { role });
      toast.success("Role updated");
      fetchUsers();
    } catch (e) { toast.error("Failed to update role"); }
  };

  const toggleStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/users/${userId}/status`, { is_active: !currentStatus });
      toast.success("Status updated");
      fetchUsers();
    } catch (e) { toast.error("Failed to update status"); }
  };

  const handleManualPayment = async () => {
    if (!manualEnrollmentId || !manualAmount) return;
    setSaving(true);
    try {
      await api.post("/payments/manual", {
        enrollment_id: manualEnrollmentId,
        amount: parseFloat(manualAmount),
        notes: manualNotes,
      });
      toast.success("Manual payment recorded");
      setManualDialog(false);
      setManualAmount("");
      setManualEnrollmentId("");
      setManualNotes("");
      setManualStudentInfo(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to record payment"); }
    setSaving(false);
  };

  const lookupEnrollment = async (enrollmentId) => {
    if (!enrollmentId || enrollmentId.length < 8) { setManualStudentInfo(null); return; }
    setLookingUp(true);
    try {
      const { data } = await api.get(`/enrollments/lookup/${enrollmentId}`);
      setManualStudentInfo(data);
    } catch {
      setManualStudentInfo(null);
    }
    setLookingUp(false);
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) return;
    setSaving(true);
    try {
      const res = await api.post("/users/create", newUser);
      const enrolled = res.data?.programmes_enrolled || 0;
      toast.success(enrolled > 0 ? `User created and enrolled into ${enrolled} programme${enrolled === 1 ? "" : "s"}` : "User created successfully");
      setCreateDialog(false);
      setNewUser({ name: "", email: "", password: "", role: "student", programme_ids: [] });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create user"); }
    setSaving(false);
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !resetPassword || resetPassword.length < 6) {
      toast.error("Password must be at least 6 characters"); return;
    }
    setSaving(true);
    try {
      await api.post("/admin/reset-password", { user_id: selectedUser.id, new_password: resetPassword });
      toast.success(`Password reset for ${selectedUser.name}`);
      setResetDialog(false);
      setResetPassword("");
      setSelectedUser(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reset password"); }
    setSaving(false);
  };

  const openEditDialog = (u) => {
    setSelectedUser(u);
    setEditForm({
      name: u.name || "",
      email: u.email || "",
      role: u.role || "student",
      phone: u.phone || "",
      school_name: u.school_name || "",
      position: u.position || "",
      interest_type: u.interest_type || ""
    });
    setEditDialog(true);
  };

  const openDetailsDialog = async (u) => {
    setSelectedUser(u);
    setDetailsDialog(true);
    setDetailsLoading(true);
    setDetailsData(null);
    try {
      const { data } = await api.get(`/users/${u.id}/details`);
      setDetailsData(data);
    } catch (e) {
      toast.error("Failed to load user details");
    }
    setDetailsLoading(false);
  };

  const handlePhotoUpload = async (file) => {
    if (!file || !selectedUser) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.put(`/users/${selectedUser.id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Profile photo updated");
      setDetailsData(d => d ? { ...d, user: { ...d.user, profile_photo: data.photo_url } } : d);
      fetchUsers();
    } catch (e) {
      toast.error("Failed to upload photo");
    }
    setUploadingPhoto(false);
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await api.put(`/users/${selectedUser.id}/edit`, editForm);
      toast.success("User updated");
      setEditDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update user"); }
    setSaving(false);
  };

  // Enrol an existing user into another programme — used from User Details dialog.
  const handleEnrollUserInProgramme = async () => {
    if (!selectedUser || !enrollProgrammeId) { toast.error("Pick a programme first"); return; }
    setEnrolling(true);
    try {
      await api.post(`/admin/users/${selectedUser.id}/enroll`, { programme_id: enrollProgrammeId });
      toast.success("User enrolled in programme");
      setEnrollProgrammeId("");
      // refresh details
      const { data } = await api.get(`/users/${selectedUser.id}/details`);
      setDetailsData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not enrol user");
    }
    setEnrolling(false);
  };

  // Suspend / unsuspend a specific enrolment from User Details dialog.
  const toggleSuspendEnrollment = async (enr) => {
    if (!enr?.id) return;
    const wantSuspend = !enr.suspended;
    let reason = "";
    if (wantSuspend) {
      reason = window.prompt(`Reason for suspending access to "${enr.programme_title || "this programme"}"? (shown to the student)`, "") || "";
    }
    setSuspendBusy(b => ({ ...b, [enr.id]: true }));
    try {
      await api.patch(`/admin/enrollments/${enr.id}/suspend`, { suspended: wantSuspend, reason });
      toast.success(wantSuspend ? "Access suspended" : "Access restored");
      setDetailsData(d => d ? {
        ...d,
        enrollments: d.enrollments.map(x => x.id === enr.id ? { ...x, suspended: wantSuspend, suspended_reason: reason } : x),
      } : d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update access");
    }
    setSuspendBusy(b => { const n = { ...b }; delete n[enr.id]; return n; });
  };

  // Source list per category (Programme drill-down uses its own list)
  const sourceList = activeCategory === "programme" && selectedProgramme
    ? programmeStudents
    : activeCategory === "pod"
      ? podSubs
      : users;

  const categoryFilter = (u) => {
    if (activeCategory === "all") return true;
    if (activeCategory === "free") return u.interest_type === "free_course";
    if (activeCategory === "programme") return true; // already drilled down
    if (activeCategory === "pod") return true; // already pod source
    return true;
  };

  const filtered = sourceList
    .filter(u =>
      categoryFilter(u) &&
      (u.name?.toLowerCase().includes(search.toLowerCase()) ||
       u.email?.toLowerCase().includes(search.toLowerCase())) &&
      (roleFilter === "all" || u.role === roleFilter) &&
      (statusFilter === "all" || (statusFilter === "active" ? u.is_active !== false : u.is_active === false)) &&
      (interestFilter === "all" ||
        (interestFilter === "none" ? !u.interest_type : u.interest_type === interestFilter))
    )
    .sort((a, b) => {
      if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "email") return (a.email || "").localeCompare(b.email || "");
      if (sortBy === "role") return (a.role || "").localeCompare(b.role || "");
      if (sortBy === "newest") return (b.created_at || "").localeCompare(a.created_at || "");
      return 0;
    });

  const uniqueRoles = [...new Set(users.map(u => u.role).filter(Boolean))];

  const exportCSV = () => {
    const headers = ["Name", "Email", "Phone", "Employer/School", "Position", "Interest Type", "Role", "Status", "Created"];
    const rows = filtered.map(u => [
      u.name || "", u.email || "", u.phone || "", u.school_name || "",
      u.position || "", u.interest_type || "", u.role || "",
      u.is_active !== false ? "Active" : "Inactive",
      u.created_at ? new Date(u.created_at).toLocaleDateString() : ""
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users-export-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} users to CSV`);
  };

  const roleColors = {
    super_admin: "bg-red-100 text-red-700",
    admin: "bg-blue-100 text-blue-700",
    instructor: "bg-emerald-100 text-emerald-700",
    student: "bg-slate-100 text-slate-700",
    finance: "bg-amber-100 text-amber-700",
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="admin-users-page">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Administration</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              User Management
            </h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={exportCSV} variant="outline" className="rounded-none gap-2 text-sm" data-testid="export-csv-btn">
              <DownloadSimple size={14} /> Export CSV
            </Button>
            <Button onClick={() => setCreateDialog(true)} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="create-user-btn">
              Create User Profile
            </Button>
            <Button onClick={() => setManualDialog(true)} variant="outline" className="rounded-none gap-2" data-testid="manual-payment-btn">
              Record Manual Payment
            </Button>
          </div>
        </div>

        <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-2 border-b border-slate-200" data-testid="user-category-tabs">
          {[
            { key: "all", label: "All Users", count: categoryCounts.all },
            { key: "free", label: "Free Students", count: categoryCounts.free },
            { key: "programme", label: "Programme Students", count: categoryCounts.programme },
            { key: "pod", label: "POD Subscribers", count: categoryCounts.pod },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveCategory(t.key)}
              data-testid={`user-tab-${t.key}`}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${activeCategory === t.key ? "border-blue-800 text-blue-900 bg-blue-50" : "border-transparent text-slate-600 hover:text-slate-900"}`}
            >
              {t.label}
              <span className={`ml-2 inline-block min-w-[20px] px-1.5 py-0.5 text-[10px] rounded-sm ${activeCategory === t.key ? "bg-blue-800 text-white" : "bg-slate-100 text-slate-700"}`}>{t.count}</span>
            </button>
          ))}
        </div>

        {activeCategory === "programme" && !selectedProgramme && (
          <div className="mb-6 p-6 border border-dashed border-slate-300 bg-slate-50" data-testid="programme-picker">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Step 1</p>
            <h3 className="text-lg font-bold mb-4 text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Select a programme to view its students</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {programmeList.map(p => (
                <button
                  key={p.id}
                  onClick={() => loadProgrammeStudents(p.id)}
                  className="text-left p-4 bg-white border border-slate-200 hover:border-blue-800 hover:shadow transition-all"
                  data-testid={`programme-card-${p.id}`}
                >
                  <p className="font-semibold text-slate-950 text-sm leading-snug mb-1">{p.title}</p>
                  <p className="text-xs text-slate-500">{p.code || ""}</p>
                </button>
              ))}
              {programmeList.length === 0 && <p className="text-sm text-slate-500">No programmes available.</p>}
            </div>
          </div>
        )}

        {activeCategory === "programme" && selectedProgramme && (
          <div className="mb-4 bg-slate-50 border border-slate-200">
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Programme</p>
                <p className="font-semibold text-slate-950">{selectedProgramme.title}</p>
                <p className="text-xs text-slate-500">{programmeStudents.length} student{programmeStudents.length === 1 ? "" : "s"}</p>
              </div>
              <Button onClick={() => { setSelectedProgramme(null); setProgrammeStudents([]); setProgStudentSearch(""); }} variant="outline" size="sm" className="rounded-none" data-testid="back-to-programme-picker">
                ← Choose another programme
              </Button>
            </div>
            {/* Round 44 — Search + eligibility toggle for programme drill-down */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 px-4 py-3">
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={progStudentSearch}
                  onChange={e => setProgStudentSearch(e.target.value)}
                  placeholder="Search name / email / admission #"
                  className="pl-9 rounded-none text-sm"
                  data-testid="programme-students-search"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer sm:col-span-2" data-testid="programme-students-eligible-toggle">
                <input
                  type="checkbox"
                  checked={progEligibleOnly}
                  onChange={e => setProgEligibleOnly(e.target.checked)}
                />
                Show only <b className="mx-1">strict-enrolled</b> students (approved application · active · paid). Uncheck to see everyone who ever applied.
              </label>
            </div>
          </div>
        )}

        {/* Round 44 — hide the generic user filter row while a programme drill-down is active
             (the drill-down has its own search/eligibility controls above) */}
        {!(activeCategory === "programme" && selectedProgramme) && (
        <div className="mb-4 flex flex-wrap gap-3 items-end">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search users..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 rounded-none"
              data-testid="search-users-input"
            />
          </div>
          <div className="w-36">
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="rounded-none h-9 text-sm" data-testid="role-filter-select">
                <Funnel size={14} className="mr-1 text-slate-400" />
                <SelectValue placeholder="All roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {uniqueRoles.map(r => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-36">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="rounded-none h-9 text-sm" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name A-Z</SelectItem>
                <SelectItem value="email">Email A-Z</SelectItem>
                <SelectItem value="role">Role</SelectItem>
                <SelectItem value="newest">Newest First</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-36">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="rounded-none h-9 text-sm" data-testid="status-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="inactive">Inactive Only</SelectItem>
                <SelectItem value="all">All Users</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Select value={interestFilter} onValueChange={setInterestFilter}>
              <SelectTrigger className="rounded-none h-9 text-sm" data-testid="interest-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Interests</SelectItem>
                <SelectItem value="programme">Programme Students</SelectItem>
                <SelectItem value="paid_course">Paid Course Students</SelectItem>
                <SelectItem value="free_course">Free Course Students</SelectItem>
                <SelectItem value="none">Not Specified</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-slate-500">{filtered.length} of {users.length} users</p>
        </div>
        )}

        {loading ? (
          <div className="h-48 bg-slate-100 animate-pulse" />
        ) : (
          <Card className="border border-slate-200 rounded-none shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wider">Name</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Email</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Phone</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Employer / School</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Position</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Role</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u, idx) => (
                  <TableRow key={u.id} data-testid={`user-row-${idx}`}>
                    <TableCell className="font-medium text-sm">
                      <button
                        onClick={() => openDetailsDialog(u)}
                        className="text-left hover:text-blue-700 hover:underline flex items-center gap-2"
                        data-testid={`view-user-${idx}`}
                      >
                        {u.profile_photo ? (
                          <img src={u.profile_photo} alt="" className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <span className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold flex items-center justify-center">
                            {(u.name || "?").slice(0,1).toUpperCase()}
                          </span>
                        )}
                        {u.name}
                      </button>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                    <TableCell className="text-sm text-slate-600">{u.phone || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-sm text-slate-600">{u.school_name || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-sm text-slate-600">{u.position || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell>
                      <Select value={u.role} onValueChange={v => updateRole(u.id, v)}>
                        <SelectTrigger className="w-32 h-8 rounded-none text-xs" data-testid={`role-select-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {allRoles.map(r => (
                            <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Badge className={`rounded-none text-xs ${u.is_active !== false ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {u.is_active !== false ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="flex gap-1 flex-wrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs h-7 text-slate-700"
                        onClick={() => openDetailsDialog(u)}
                        data-testid={`details-user-${idx}`}
                      >
                        <Eye size={13} className="mr-1" /> View
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs h-7 text-blue-700"
                        onClick={() => openEditDialog(u)}
                        data-testid={`edit-user-${idx}`}
                      >
                        <PencilSimple size={13} className="mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs h-7"
                        onClick={() => toggleStatus(u.id, u.is_active !== false)}
                        data-testid={`toggle-status-${idx}`}
                      >
                        {u.is_active !== false ? "Deactivate" : "Activate"}
                      </Button>
                      {user?.role === "super_admin" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs h-7 text-blue-700"
                        onClick={() => { setSelectedUser(u); setResetDialog(true); }}
                        data-testid={`reset-pwd-${idx}`}
                      >
                        <LockKey size={13} className="mr-1" /> Reset
                      </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Create User Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="rounded-none">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Create User Profile</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-sm">Full Name</Label>
                <Input value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} className="mt-1 rounded-none" placeholder="Full name" data-testid="create-user-name" />
              </div>
              <div>
                <Label className="text-sm">Email</Label>
                <Input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} className="mt-1 rounded-none" placeholder="user@example.com" data-testid="create-user-email" />
              </div>
              <div>
                <Label className="text-sm">Password</Label>
                <Input type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} className="mt-1 rounded-none" placeholder="Minimum 6 characters" data-testid="create-user-password" />
              </div>
              <div>
                <Label className="text-sm">Role</Label>
                <Select value={newUser.role} onValueChange={v => setNewUser({...newUser, role: v})}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="create-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allRoles.map(r => (
                      <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="border border-slate-200 p-3 max-h-56 overflow-y-auto" data-testid="create-user-programmes">
                <Label className="text-sm font-bold uppercase tracking-widest text-slate-600">Assign to Programmes (optional)</Label>
                <p className="text-[11px] text-slate-500 mt-0.5 mb-2">For offline-paid students. Each selected programme gets an enrollment record with payment_status &lsquo;paid_offline&rsquo; and application_status &lsquo;approved&rsquo; — they&apos;ll see it on their dashboard immediately.</p>
                {programmeList.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No programmes available yet.</p>
                ) : (
                  <div className="space-y-1">
                    {programmeList.map(p => {
                      const checked = newUser.programme_ids.includes(p.id);
                      return (
                        <label key={p.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-1 py-1" data-testid={`create-user-prog-${p.id}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e => {
                              setNewUser(u => ({
                                ...u,
                                programme_ids: e.target.checked
                                  ? [...u.programme_ids, p.id]
                                  : u.programme_ids.filter(x => x !== p.id),
                              }));
                            }}
                          />
                          <span className="font-mono text-amber-700">{p.code || "—"}</span>
                          <span className="truncate text-slate-700">{p.title}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {newUser.programme_ids.length > 0 && (
                  <p className="text-[11px] text-emerald-700 mt-2 font-semibold">{newUser.programme_ids.length} programme{newUser.programme_ids.length === 1 ? "" : "s"} selected</p>
                )}
              </div>
              <Button onClick={handleCreateUser} disabled={saving || !newUser.name || !newUser.email || !newUser.password} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="confirm-create-user-btn">
                {saving ? "Creating..." : "Create User"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Manual Payment Dialog */}
        <Dialog open={manualDialog} onOpenChange={setManualDialog}>
          <DialogContent className="rounded-none">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Record Manual Payment</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <p className="text-sm text-slate-600">Use this for bank transfer verifications or offline payments.</p>
              <div>
                <Label className="text-sm">Enrollment ID</Label>
                <Input
                  value={manualEnrollmentId}
                  onChange={e => {
                    setManualEnrollmentId(e.target.value);
                    lookupEnrollment(e.target.value);
                  }}
                  className="mt-1 rounded-none"
                  placeholder="Paste enrollment ID"
                  data-testid="manual-enrollment-input"
                />
                {lookingUp && <p className="text-xs text-blue-600 mt-1">Looking up...</p>}
                {manualStudentInfo && (
                  <div className="mt-2 bg-emerald-50 border border-emerald-200 p-3 text-sm" data-testid="enrollment-lookup-result">
                    <p className="font-semibold text-emerald-800">{manualStudentInfo.student_name}</p>
                    <p className="text-xs text-slate-600">{manualStudentInfo.student_email}</p>
                    <p className="text-xs text-slate-500 mt-1">Programme: {manualStudentInfo.programme_title}</p>
                    <p className="text-xs text-slate-500">Balance: ${manualStudentInfo.balance?.toLocaleString()}</p>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-sm">Amount (USD)</Label>
                <Input type="number" value={manualAmount} onChange={e => setManualAmount(e.target.value)} className="mt-1 rounded-none" data-testid="manual-amount-input" />
              </div>
              <div>
                <Label className="text-sm">Notes</Label>
                <Input value={manualNotes} onChange={e => setManualNotes(e.target.value)} className="mt-1 rounded-none" placeholder="e.g. Bank transfer ref #123" />
              </div>
              <Button onClick={handleManualPayment} disabled={saving || !manualEnrollmentId || !manualAmount} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="confirm-manual-payment-btn">
                {saving ? "Recording..." : "Record Payment"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Admin Reset Password Dialog */}
        <Dialog open={resetDialog} onOpenChange={(open) => { setResetDialog(open); if (!open) { setResetPassword(""); setSelectedUser(null); } }}>
          <DialogContent className="rounded-none sm:max-w-sm">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Reset Password</DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-3 mt-2">
                <p className="text-sm text-slate-600">Reset password for <span className="font-semibold">{selectedUser.name}</span> ({selectedUser.email})</p>
                <div>
                  <Label className="text-sm">New Password</Label>
                  <Input type="password" value={resetPassword} onChange={e => setResetPassword(e.target.value)} className="mt-1 rounded-none" placeholder="Min 6 characters" data-testid="admin-reset-password-input" />
                </div>
                <Button onClick={handleResetPassword} disabled={saving || !resetPassword || resetPassword.length < 6} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="confirm-reset-password-btn">
                  {saving ? "Resetting..." : "Save New Password"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editDialog} onOpenChange={(open) => { setEditDialog(open); if (!open) setSelectedUser(null); }}>
          <DialogContent className="rounded-none sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Edit User Profile</DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Full Name</Label>
                    <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} className="mt-1 rounded-none" data-testid="edit-user-name" />
                  </div>
                  <div>
                    <Label className="text-sm">Email</Label>
                    <Input type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} className="mt-1 rounded-none" data-testid="edit-user-email" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Phone</Label>
                    <Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} className="mt-1 rounded-none" placeholder="+234 ..." data-testid="edit-user-phone" />
                  </div>
                  <div>
                    <Label className="text-sm">Role</Label>
                    <Select value={editForm.role} onValueChange={v => setEditForm({...editForm, role: v})}>
                      <SelectTrigger className="mt-1 rounded-none" data-testid="edit-user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {allRoles.map(r => (
                          <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Current Employer / School Name</Label>
                  <Input value={editForm.school_name} onChange={e => setEditForm({...editForm, school_name: e.target.value})} className="mt-1 rounded-none" placeholder="e.g. Greenfield Academy" data-testid="edit-user-employer" />
                </div>
                <div>
                  <Label className="text-sm">Role / Position at Work</Label>
                  <Input value={editForm.position} onChange={e => setEditForm({...editForm, position: e.target.value})} className="mt-1 rounded-none" placeholder="e.g. Principal, Head of School" data-testid="edit-user-position" />
                </div>
                <div>
                  <Label className="text-sm">Interest Type</Label>
                  <Select value={editForm.interest_type || "none"} onValueChange={v => setEditForm({...editForm, interest_type: v === "none" ? "" : v})}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="edit-user-interest">
                      <SelectValue placeholder="Not specified" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      <SelectItem value="programme">Programme</SelectItem>
                      <SelectItem value="paid_course">Paid Course</SelectItem>
                      <SelectItem value="free_course">Free Course</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleEditUser} disabled={saving} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="confirm-edit-user-btn">
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* User Details (Deep-Dive) Dialog */}
        <Dialog open={detailsDialog} onOpenChange={(open) => { setDetailsDialog(open); if (!open) { setSelectedUser(null); setDetailsData(null); } }}>
          <DialogContent className="rounded-none sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-details-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>User Profile</DialogTitle>
            </DialogHeader>
            {detailsLoading && <p className="text-sm text-slate-500">Loading…</p>}
            {detailsData && (
              <div className="space-y-5 mt-2">
                {/* Header with photo */}
                <div className="flex gap-4 items-start pb-4 border-b border-slate-200">
                  <div className="relative">
                    {detailsData.user.profile_photo ? (
                      <img src={detailsData.user.profile_photo} alt="" className="w-20 h-20 rounded-full object-cover border border-slate-200" />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-slate-200 text-slate-600 text-2xl font-bold flex items-center justify-center">
                        {(detailsData.user.name || "?").slice(0,1).toUpperCase()}
                      </div>
                    )}
                    <label className="absolute -bottom-1 -right-1 bg-blue-800 text-white rounded-full p-1.5 cursor-pointer hover:bg-blue-900" title="Upload photo">
                      <UploadSimple size={12} />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                        data-testid="upload-photo-input"
                      />
                    </label>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>{detailsData.user.name}</h3>
                    <p className="text-sm text-slate-600">{detailsData.user.email}</p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <Badge className={`rounded-none text-[10px] ${roleColors[detailsData.user.role] || "bg-slate-100 text-slate-700"}`}>
                        {(detailsData.user.role || "").replace("_", " ")}
                      </Badge>
                      <Badge className={`rounded-none text-[10px] ${detailsData.user.is_active !== false ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {detailsData.user.is_active !== false ? "Active" : "Inactive"}
                      </Badge>
                      {uploadingPhoto && <span className="text-[10px] text-blue-600">Uploading…</span>}
                    </div>
                  </div>
                </div>

                {/* Contact + work details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex gap-2 items-start">
                    <Phone size={14} className="text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-500">Phone</p>
                      <p className="text-slate-800">{detailsData.user.phone || "—"}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 items-start">
                    <Buildings size={14} className="text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-500">Employer / School</p>
                      <p className="text-slate-800">{detailsData.user.school_name || "—"}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 items-start">
                    <Briefcase size={14} className="text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-500">Position</p>
                      <p className="text-slate-800">{detailsData.user.position || "—"}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 items-start">
                    <GraduationCap size={14} className="text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-500">Interest</p>
                      <p className="text-slate-800">{detailsData.user.interest_type ? detailsData.user.interest_type.replace("_", " ") : "—"}</p>
                    </div>
                  </div>
                </div>

                {/* Enrollments */}
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Enrollments ({detailsData.enrollments.length})</h4>
                  {detailsData.enrollments.length === 0 ? (
                    <p className="text-sm text-slate-400">No programme enrollments.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {detailsData.enrollments.map((e, i) => (
                        <div key={i} className={`flex justify-between items-center border px-3 py-2 text-xs ${e.suspended ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`} data-testid={`details-enrollment-${i}`}>
                          <span className="font-medium text-slate-800 truncate">{e.programme_title || e.programme_id}</span>
                          <span className="flex gap-2 items-center flex-wrap justify-end">
                            <Badge className={`rounded-none text-[10px] ${e.suspended ? "bg-red-700 text-white" : "bg-slate-200 text-slate-700"}`}>{e.suspended ? "suspended" : e.status}</Badge>
                            {typeof e.balance === "number" && e.balance > 0 && (
                              <span className="text-amber-700">Bal: ${e.balance.toLocaleString()}</span>
                            )}
                            <button
                              type="button"
                              disabled={!!suspendBusy[e.id]}
                              onClick={() => toggleSuspendEnrollment(e)}
                              className={`text-[11px] underline underline-offset-2 disabled:opacity-50 ${e.suspended ? "text-emerald-700 hover:text-emerald-900" : "text-amber-700 hover:text-amber-900"}`}
                              data-testid={`toggle-suspend-enrollment-${i}`}
                            >
                              {suspendBusy[e.id] ? "…" : (e.suspended ? "Unsuspend" : "Suspend")}
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                if (!window.confirm(`Remove ${detailsData.user.name} from "${e.programme_title}"? This deletes the enrolment + any application.`)) return;
                                try {
                                  await api.delete(`/admin/enrollments/${e.id}`);
                                  toast.success("Student removed from programme");
                                  setDetailsData({ ...detailsData, enrollments: detailsData.enrollments.filter(x => x.id !== e.id) });
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || "Remove failed");
                                }
                              }}
                              className="text-red-600 hover:text-red-800 text-[11px] underline underline-offset-2"
                              data-testid={`remove-enrollment-${i}`}
                            >
                              Remove
                            </button>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Enrol this user into ANOTHER programme */}
                  {detailsData.user.role === "student" && (
                    <div className="mt-3 border border-blue-200 bg-blue-50/40 p-3" data-testid="enroll-user-in-programme-panel">
                      <p className="text-[10px] uppercase tracking-widest text-blue-900 font-bold mb-2">Enrol in another programme</p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Select value={enrollProgrammeId} onValueChange={setEnrollProgrammeId}>
                          <SelectTrigger className="rounded-none flex-1" data-testid="enroll-programme-select">
                            <SelectValue placeholder="Pick a programme…" />
                          </SelectTrigger>
                          <SelectContent>
                            {programmeList
                              .filter(p => !detailsData.enrollments.some(en => en.programme_id === p.id))
                              .filter(p => (p.status || "").toLowerCase() !== "draft")
                              .map(p => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.code ? `${p.code} · ` : ""}{p.title}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <Button
                          onClick={handleEnrollUserInProgramme}
                          disabled={!enrollProgrammeId || enrolling}
                          className="bg-blue-800 hover:bg-blue-900 text-white rounded-none gap-2 whitespace-nowrap"
                          data-testid="enroll-user-confirm-btn"
                        >
                          {enrolling ? "Enrolling…" : "Enrol user"}
                        </Button>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1.5">Creates a <strong>pending_payment</strong> enrolment — student sees the programme on their dashboard with a Pay Now CTA. The programme must be currently enrolling (catalogue status active / enrollment_open).</p>
                    </div>
                  )}
                </div>

                {/* Progress */}
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Course Progress ({detailsData.progress.length})</h4>
                  {detailsData.progress.length === 0 ? (
                    <p className="text-sm text-slate-400">No course activity yet.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {detailsData.progress.map((p, i) => (
                        <div key={i} className="flex justify-between bg-slate-50 border border-slate-200 px-3 py-2 text-xs" data-testid={`details-progress-${i}`}>
                          <span className="font-medium text-slate-800">{p.course_title}</span>
                          <span className="text-slate-600">{p.lessons_completed} / {p.lessons_tracked} lessons completed</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Certificates */}
                {detailsData.certificates?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Certificates ({detailsData.certificates.length})</h4>
                    <div className="space-y-1.5">
                      {detailsData.certificates.map((c, i) => (
                        <div key={i} className="flex justify-between bg-amber-50 border border-amber-200 px-3 py-2 text-xs">
                          <span className="font-medium text-amber-900">{c.cert_number || c.id}</span>
                          <span className="text-amber-700">{c.awarded_at ? new Date(c.awarded_at).toLocaleDateString() : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Feedback submissions — Round 42 · Task 3 */}
                {detailsData.feedback?.length > 0 && (
                  <div data-testid="details-feedback-section">
                    <h4 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Feedback submissions ({detailsData.feedback.length})</h4>
                    <div className="space-y-2">
                      {detailsData.feedback.map((f, i) => (
                        <div key={i} className="border border-slate-200 bg-slate-50 px-3 py-2 text-xs space-y-1" data-testid={`details-feedback-${i}`}>
                          <div className="flex justify-between items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-800 truncate">{f.course_title || f.course_id}</span>
                            <span className="text-amber-700">Overall {Number(f.overall_rating || f.rating || 0)}/5</span>
                          </div>
                          <div className="flex flex-wrap gap-x-3 text-[10px] text-slate-500">
                            {f.content_quality > 0 && <span>Content {f.content_quality}/5</span>}
                            {f.instructor_rating > 0 && <span>Instructor {f.instructor_rating}/5</span>}
                            {f.pace_rating > 0 && <span>Pace {f.pace_rating}/5</span>}
                            {typeof f.would_recommend === "boolean" && <span>Recommend: {f.would_recommend ? "Yes" : "No"}</span>}
                          </div>
                          {f.most_valuable && (
                            <p className="text-slate-700"><span className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold">Most valuable · </span>{f.most_valuable}</p>
                          )}
                          {f.improvements && (
                            <p className="text-slate-700"><span className="text-[9px] uppercase tracking-widest text-amber-700 font-bold">Improvements · </span>{f.improvements}</p>
                          )}
                          {(f.additional_comments || f.comment) && (
                            <p className="text-slate-700"><span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Comments · </span>{f.additional_comments || f.comment}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2 border-t border-slate-200">
                  <Button
                    onClick={() => { setDetailsDialog(false); openEditDialog(selectedUser); }}
                    className="flex-1 bg-blue-800 text-white hover:bg-blue-900 rounded-none"
                    data-testid="details-edit-btn"
                  >
                    <PencilSimple size={14} className="mr-1" /> Edit Profile
                  </Button>
                  {user?.role === "super_admin" && detailsData.user.role !== "super_admin" && (
                    <Button
                      variant="outline"
                      onClick={async () => {
                        if (!window.confirm(`Permanently delete user "${detailsData.user.name}" (${detailsData.user.email})? All their enrolments, applications, and POD subscriptions will be removed. This cannot be undone.`)) return;
                        try {
                          await api.delete(`/admin/users/${detailsData.user.id}`);
                          toast.success("User deleted");
                          setDetailsDialog(false);
                          setUsers(users.filter(u => u.id !== detailsData.user.id));
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Delete failed");
                        }
                      }}
                      className="rounded-none border-red-300 text-red-700 hover:bg-red-50"
                      data-testid="details-delete-user-btn"
                    >
                      <Trash size={14} className="mr-1" /> Delete user
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
